import React, { useCallback, useEffect, useState } from 'react';
import { Nav, NavItem, NavLink } from 'reactstrap';
import moment from 'moment';

import { partial } from '../../../util/translation/translation';

import DisplayCounter from '../../_uiComponents/DisplayCounter';
import useConfig from '../../../util/useConfig';
import { formatCurrencyNumber } from '../../../util/functions';
import Api from '../../../util/api';

import ChartLineVariantIcon from 'mdi-react/ChartLineVariantIcon';
import PulseIcon from 'mdi-react/PulseIcon';
import CreditCardRemoveIcon from 'mdi-react/CreditCardOffIcon';
import CreditCardIcon from 'mdi-react/CreditCardMultipleIcon';
import CashMultipleIcon from 'mdi-react/CashMultipleIcon';
import BankTransferIcon from 'mdi-react/BankTransferIcon';
import PercentIcon from 'mdi-react/PercentIcon';
import Loading from '../../../shared/components/custom/Loading';

const TransactionsAnalytics = () => {
  const config = useConfig();
  const s = partial('shared');
  const d = partial('Deals');
  const p = partial('transactions');
  const c = partial('connect');
  const getEntityName = (entityType) => {
    switch (entityType) {
      case 'tier':
        return p('tier');
      case 'auction_item':
        return p('auctionItem');
      case 'goal_subscription':
        return p('goalSponsor');
      case 'lottery_ticket':
        return p('lotteryTicket');
      default:
        return '';
    }
  };
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();

  const [startEndObject, setStartEndObject] = useState({
    start: null,
    end: null,
    type: 'allTime',
  });
  const handleStartEnd = (filter) => {
    let internal = {
      start: null,
      end: null,
      type: filter,
    };
    switch (filter) {
      case 'allTime':
        internal.start = null;
        internal.end = null;
        break;
      case 'thisYear':
        internal.start = moment().startOf('year').toISOString();
        internal.end = moment().endOf('year').toISOString();
        break;
      case 'lastYear':
        internal.start = moment()
          .subtract(1, 'year')
          .startOf('year')
          .toISOString();
        internal.end = moment().subtract(1, 'year').endOf('year').toISOString();
        break;
      default:
        internal.start = null;
        internal.end = null;
        break;
    }
    setStartEndObject(internal);
  };

  const fetchTransactionsStatistics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Api.transactions.getTransactionsStatistics({
        startDate: startEndObject.start,
        endDate: startEndObject.end,
      });
      setData(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }, [startEndObject.end, startEndObject.start]);
  useEffect(() => {
    fetchTransactionsStatistics();
  }, [fetchTransactionsStatistics]);

  const money = (ore) =>
    `${formatCurrencyNumber(((ore || 0) / 100).toFixed(2))} ${config.currencyCaps}`;
  // Stripe Connect's share of a bucket; nothing for clubs without Connect
  const connectCounters = (item) =>
    item?.connectTurnover > 0 ? (
      <>
        <DisplayCounter
          id="connect-turnover"
          outline
          title={c('earningsConnectTurnover')}
          value={money(item.connectTurnover)}
          icon={<BankTransferIcon style={{ width: 18 }} />}
        />
        <DisplayCounter
          id="platform-fee"
          outline
          title={c('earningsPlatformFee')}
          value={money(item.platformFee)}
          icon={<PercentIcon style={{ width: 18 }} />}
        />
      </>
    ) : null;

  const Items = () => {
    const itemEntries = Object.entries(data || {}).filter(
      ([key]) => key !== 'total'
    );

    return itemEntries.length ? (
      itemEntries.map(([key, item]) => {
        const aov = item?.transactions ? item.turnover / item.transactions : 0;

        return (
          <div className="my-4" key={key}>
            <h4 className="mb-2 bold">{getEntityName(key)}</h4>
            <DisplayCounter
              id="sales"
              outline
              title={p('turnover')}
              value={`${formatCurrencyNumber((item?.turnover / 100).toFixed(2))} ${config.currencyCaps}`}
              icon={<ChartLineVariantIcon style={{ width: 18 }} />}
            />

            <DisplayCounter
              id="leads"
              outline
              title={p('completeTransactions')}
              value={`${item?.transactions || 0}`}
              icon={<CreditCardIcon style={{ width: 18 }} />}
            />

            <DisplayCounter
              id="leads"
              title={p('aov')}
              value={`${formatCurrencyNumber((aov / 100).toFixed(2))} ${config.currencyCaps}`}
              icon={<CashMultipleIcon style={{ width: 18 }} />}
            />
            {connectCounters(item)}
          </div>
        );
      })
    ) : (
      <div></div>
    );
  };

  return (
    <>
      <Loading loading={loading} />
      <Nav pills>
        <NavItem>
          <NavLink
            role="button"
            onClick={() => handleStartEnd('allTime')}
            active={startEndObject.type === 'allTime'}
          >
            {s('allTime')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            role="button"
            onClick={() => handleStartEnd('thisYear')}
            active={startEndObject.type === 'thisYear'}
          >
            {s('thisYear')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            role="button"
            onClick={() => handleStartEnd('lastYear')}
            active={startEndObject.type === 'lastYear'}
          >
            {s('lastYear')}
          </NavLink>
        </NavItem>
      </Nav>
      <div className="my-2">
        <DisplayCounter
          id="sales"
          color="primary"
          title={d('totalBusinessValue')}
          value={`${formatCurrencyNumber((data?.total?.turnover / 100).toFixed(2))} ${config.currencyCaps}`}
          icon={<ChartLineVariantIcon style={{ width: 18 }} />}
        />

        <DisplayCounter
          id="leads"
          color="success"
          title={p('completeTransactions')}
          value={`${data?.total?.transactions || 0}`}
          icon={<PulseIcon style={{ width: 18 }} />}
        />
        <DisplayCounter
          id="leads"
          title={p('incompleteTransactions')}
          value={`${(
            ((data?.total?.incompleteTransactions || 0) /
              ((data?.total?.incompleteTransactions || 0) +
                (data?.total?.transactions || 0) || 1)) *
            100
          ).toFixed(2)}%`}
          icon={<CreditCardRemoveIcon style={{ width: 18 }} />}
        />
        {connectCounters(data?.total)}
      </div>
      <div className="my-2">
        <Items />
      </div>
    </>
  );
};

export default TransactionsAnalytics;
